import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Plus, UserX, Trash2, Loader2, ShieldCheck, RefreshCw } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = ["admin", "manager", "team_lead", "agent"];
const ROLE_META = {
  admin: { label: "Super User", color: "#C23A00" },
  manager: { label: "Manager", color: "#8A6D00" },
  team_lead: { label: "Team Lead", color: "#1D4ED8" },
  agent: { label: "Agent", color: "#0E6B3F" },
};

function RoleBadge({ role }) {
  const m = ROLE_META[role] || ROLE_META.agent;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ background: `${m.color}1A`, color: m.color }}>
      {role === "admin" && <ShieldCheck className="w-3 h-3" />} {m.label}
    </span>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const emptyNew = { id: "", name: "", email: "", extension: "", teams_handle: "", role: "agent" };
  const [newUser, setNewUser] = useState(emptyNew);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/users`);
      setUsers(res.data.users || []);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "You don't have access to Administration" : "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const patchUser = async (id, updates, optimisticMsg) => {
    try {
      await axios.put(`${API}/admin/users/${id}`, updates);
      setUsers(prev => prev.map(u => u.id === id ? { ...u, ...updates } : u));
      if (optimisticMsg) toast.success(optimisticMsg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
      fetchUsers();
    }
  };

  const changeRole = (id, role) =>
    patchUser(id, { role }, "Role updated — takes effect on their next login");

  const toggleActive = (u) =>
    patchUser(u.id, { active: !u.active }, !u.active ? "User activated" : "User deactivated");

  const removeUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}?hard=true`);
      setUsers(prev => prev.filter(x => x.id !== u.id));
      toast.success("User deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const createUser = async () => {
    if (!newUser.id.trim() || !newUser.name.trim()) {
      toast.error("ID and Name are required");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/users`, newUser);
      setUsers(prev => [...prev, res.data.user].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      toast.success("User created");
      setAddOpen(false);
      setNewUser(emptyNew);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-users-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FFF8D6] flex items-center justify-center">
            <Users className="w-5 h-5 text-[#8A6D00]" />
          </div>
          <div>
            <h2 className="heading text-xl text-[#1A1A1A] tracking-tight">Users</h2>
            <p className="text-sm text-[#59554D]">{users.length} staff members · role changes apply on next login</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchUsers} disabled={loading}
            className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED]" data-testid="admin-users-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button onClick={() => setAddOpen(true)} className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A]" data-testid="admin-add-user-btn">
            <Plus className="w-4 h-4 mr-1" /> Add User
          </Button>
        </div>
      </div>

      <Card className="bg-[#FFFFFF] border-[#DAD5CA]">
        <CardContent className="p-0">
          <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#DAD5CA] text-[11px] uppercase tracking-wider text-[#8A857A] font-semibold">
            <div className="col-span-4">User</div>
            <div className="col-span-3">Role</div>
            <div className="col-span-2">Active</div>
            <div className="col-span-3 text-right">Actions</div>
          </div>
          {loading ? (
            <div className="p-10 text-center text-[#8A857A]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : users.map(u => (
            <div key={u.id} className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-[#F4F2ED] items-center" data-testid={`admin-user-row-${u.id}`}>
              <div className="col-span-4 min-w-0">
                <div className="text-[#1A1A1A] font-medium truncate flex items-center gap-2">
                  {u.name} <RoleBadge role={u.role} />
                </div>
                <div className="text-xs text-[#8A857A] truncate">{u.email || "no email"}{u.extension ? ` · ext ${u.extension}` : ""}</div>
              </div>
              <div className="col-span-3">
                <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                  <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A] h-9 w-40" data-testid={`admin-role-select-${u.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                    {ROLES.map(r => <SelectItem key={r} value={r}>{ROLE_META[r].label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Switch checked={!!u.active} onCheckedChange={() => toggleActive(u)} data-testid={`admin-active-${u.id}`} />
              </div>
              <div className="col-span-3 flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => toggleActive(u)}
                  className="border-[#DAD5CA] bg-transparent text-[#59554D] hover:bg-[#F4F2ED] h-8"
                  data-testid={`admin-deactivate-${u.id}`}>
                  <UserX className="w-3.5 h-3.5 mr-1" /> {u.active ? "Deactivate" : "Activate"}
                </Button>
                <Button size="icon" variant="ghost" onClick={() => removeUser(u)}
                  className="h-8 w-8 text-[#8A857A] hover:text-[#C23A00]" data-testid={`admin-delete-${u.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#DAD5CA] text-[#1A1A1A]" data-testid="admin-add-user-dialog">
          <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#59554D]">User ID *</label>
                <Input value={newUser.id} onChange={(e) => setNewUser({ ...newUser, id: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                  placeholder="e.g. jane_doe" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="admin-new-id" />
              </div>
              <div>
                <label className="text-xs text-[#59554D]">Full Name *</label>
                <Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="Jane Doe" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="admin-new-name" />
              </div>
              <div>
                <label className="text-xs text-[#59554D]">Email</label>
                <Input value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="jane@equipr.software" className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="admin-new-email" />
              </div>
              <div>
                <label className="text-xs text-[#59554D]">Extension</label>
                <Input value={newUser.extension} onChange={(e) => setNewUser({ ...newUser, extension: e.target.value })}
                  className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="admin-new-ext" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#59554D]">Role</label>
              <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]" data-testid="admin-new-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                  {ROLES.map(r => <SelectItem key={r} value={r}>{ROLE_META[r].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)} className="text-[#59554D]">Cancel</Button>
            <Button onClick={createUser} disabled={saving} className="bg-[#FFD108] hover:bg-[#FFF8D6] text-[#1A1A1A]" data-testid="admin-create-user-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />} Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
